import { tableOptions, filterOptions } from '@/utils/table.util'
import {
  getFormFieldsApi,
  setFormFieldsApi,
  getFormStaticsApi,
  setFormStaticsApi,
  getReviewFormsApi,
  getFormReviewApi,
  getFormApi,
  putFormSettingsApi,
  putFormTranslationsApi,
  getConditionableFieldsApi,
  getFormDefaultTranslations,
  getFormSubmissionApi,
  getReviewApi,
  getReviewExportApi,
  getReviewsFiltersApi,
} from '@/services/review.service'
import { downloadFile, getFileName, getShortLocal } from '@/utils/utilities.util'
import i18n from '@/i18n/i18n'
import { defaultReviewLexicon } from '@/config/reviewLexicon.config'
import { getPropertyByLocale } from '@/utils/helper.util'
import { defaultLangList } from '@/config/formTranslations.config'
import {
  getFiltersParams,
  flattenReviewList,
  formatReviewHeaders,
} from '@/domains/review/services/reviewListTransform.service'
import { flattenReview } from '@/domains/review/services/reviewTransform.service'

const GET_REVIEW_FORMS = 'GET_REVIEW_FORMS'
const GET_FORM_REVIEWS = 'GET_FORM_REVIEWS'
const SET_CURRENT_REVIEW = 'SET_CURRENT_REVIEW'
const GET_CONDITIONABLE_FIELDS = 'GET_CONDITIONABLE_FIELDS'
const GET_CURRENT_FORMS_STATICS = 'GET_CURRENT_FORMS_STATICS'
const GET_CURRENT_FORMS_FIELDS = 'GET_CURRENT_FORMS_FIELDS'
const SET_CURRENT_FORM = 'SET_CURRENT_FORM'
const SET_CURRENT_FORM_FIELDS = 'SET_CURRENT_FORM_FIELDS'
const SET_CURRENT_FORM_STATICS = 'SET_CURRENT_FORM_STATICS'
const SET_CURRENT_FORM_TRANSLATIONS = 'SET_CURRENT_FORM_TRANSLATIONS'
const SET_CURRENT_FORM_SETTINGS = 'SET_CURRENT_FORM_SETTINGS'
const SET_CURRENT_FORM_LEXICON = 'SET_CURRENT_FORM_LEXICON'
const SET_CURRENT_LOCALE_TAB = 'SET_CURRENT_LOCALE_TAB'
const SET_FORM_DEFAULT_TRANSLATIONS = 'SET_FORM_DEFAULT_TRANSLATIONS'
const SET_FORM_MANDATORY_TRANSLATIONS = 'SET_FORM_MANDATORY_TRANSLATIONS'
const SET_ACTION_DRAWER_OPENED = 'SET_ACTION_DRAWER_OPENED'
const SET_CURRENT_ACTION_DRAWER = 'SET_CURRENT_ACTION_DRAWER'
const SET_CURRENT_FORM_SUBMISSION = 'SET_CURRENT_FORM_SUBMISSION'
const SET_CURRENT_FILTERS = 'SET_CURRENT_FILTERS'
const SET_SEARCH = 'SET_SEARCH'
const SET_REVIEWS_GENERIC_FILTER = 'SET_REVIEWS_GENERIC_FILTER'
const SET_REVIEWS_FIELD_FILTERS = 'SET_REVIEWS_FIELD_FILTERS'

const getDefaultState = () => {
  return {
    formsList: {
      total: null,
      rows: [],
      paginated: null,
    },
    currentFilters: [],
    currentReviewList: {
      itemsPerPage: null,
      total: null,
      paginated: null,
      rows: [],
      headers: [],
    },
    administrators: [],
    currentReview: null,
    currentFormSubmission: null,
    currentForm: null,
    currentFormFields: [],
    currentFormStatics: [],
    currentFormLexicon: null,
    genericFilters: [],
    fieldFilters: [],
    currentDateRange: null,
    search: '',
    conditionableFields: null,
    dynamicTranslations: {},
    conversionDashboard: {},
    actionDrawerOpened: false,
    currentActionDrawer: null,
    currentLocaleTab: null,
    defaultTranslations: {},
    mandatoryTranslations: [],
  }
}

const state = getDefaultState()

const mutations = {
  [GET_REVIEW_FORMS]: (state, formsList) => {
    state.formsList = formsList
  },
  [GET_FORM_REVIEWS]: (state, currentReviewList) => {
    state.currentReviewList = currentReviewList
  },
  [SET_ACTION_DRAWER_OPENED]: (state, actionDrawerOpened) => {
    state.actionDrawerOpened = actionDrawerOpened
  },
  [SET_CURRENT_ACTION_DRAWER]: (state, currentActionDrawer) => {
    state.currentActionDrawer = currentActionDrawer
  },
  [SET_SEARCH]: (state, search) => {
    state.search = search
  },
  [SET_REVIEWS_GENERIC_FILTER]: (state, genericFilters) => {
    state.genericFilters = genericFilters
  },
  [SET_REVIEWS_FIELD_FILTERS]: (state, fieldFilters) => {
    state.fieldFilters = fieldFilters
  },
  [GET_CONDITIONABLE_FIELDS]: (state, conditionableFields) => {
    state.conditionableFields = conditionableFields
  },
  [SET_CURRENT_FORM]: (state, currentForm) => {
    state.currentForm = currentForm
  },
  [GET_CURRENT_FORMS_FIELDS]: (state, formFields) => {
    state.currentFormFields = formFields
  },
  [SET_CURRENT_FORM_FIELDS]: (state, currentFormFields) => {
    state.currentFormFields = currentFormFields
  },
  [SET_CURRENT_FORM_STATICS]: (state, currentFormStatics) => {
    state.currentFormStatics = currentFormStatics
  },
  [GET_CURRENT_FORMS_STATICS]: (state, formStatics) => {
    state.currentFormStatics = formStatics
  },
  [SET_CURRENT_FORM_TRANSLATIONS]: (state, translations) => {
    state.currentForm.translations = translations
  },
  [SET_CURRENT_FORM_SETTINGS]: (state, settings) => {
    state.currentForm.settings = settings
  },
  [SET_CURRENT_FORM_LEXICON]: (state, currentFormLexicon) => {
    state.currentFormLexicon = currentFormLexicon
  },
  [SET_CURRENT_LOCALE_TAB]: (state, currentLocaleTab) => {
    state.currentLocaleTab = currentLocaleTab
  },
  [SET_FORM_DEFAULT_TRANSLATIONS]: (state, defaultTranslations) => {
    state.defaultTranslations = defaultTranslations
  },
  [SET_FORM_MANDATORY_TRANSLATIONS]: (state, mandatoryTranslations) => {
    state.mandatoryTranslations = mandatoryTranslations
  },
  [SET_CURRENT_FORM_SUBMISSION]: (state, currentFormSubmission) => {
    state.currentFormSubmission = currentFormSubmission
  },
  [SET_CURRENT_REVIEW]: (state, currentReview) => {
    state.currentReview = currentReview
  },
  [SET_CURRENT_FILTERS]: (state, currentFilters) => {
    state.currentFilters = currentFilters
  },
}

const actions = {
  async getFormsList({ commit, rootState }) {
    const formsList = await getReviewFormsApi(
      rootState.backoffice.currentClient.identifier,
      getShortLocal(rootState.backoffice.currentUser.locale)
    )
    commit(GET_REVIEW_FORMS, formsList)
  },

  async getReviewsList({ commit, dispatch, rootState, state }, { formId, tableParams }) {
    if (state.currentForm?.id !== formId) {
      await dispatch('setCurrentForm', formId)
    }

    const urlParams = tableOptions(tableParams, 'reviews')
    const formattedFilter = getFiltersParams(state.currentFilters)
    const filterParams = filterOptions(
      { ...formattedFilter, search: state.search, sortBy: 'moderation.startAt', sortDirection: 'DESC' },
      'reviews'
    )

    const reviewsList = await getFormReviewApi(
      rootState.backoffice.currentClient.identifier,
      formId,
      urlParams,
      filterParams
    )
    const formattedReview = {
      ...reviewsList,
      rows: flattenReviewList(reviewsList.rows),
      headers: formatReviewHeaders(reviewsList.headers, state.currentFormLexicon),
    }

    commit(GET_FORM_REVIEWS, formattedReview)
  },
  setSearch({ commit }, search) {
    commit(SET_SEARCH, search)
  },

  async getReviewExport({ dispatch, rootState, state }, { formId, tableParams }) {
    dispatch('backoffice/setWorking', true, { root: true })

    const urlParams = tableOptions(tableParams, 'reviews')
    const formattedFilter = getFiltersParams(state.currentFilters)
    const filterParams = filterOptions({ ...formattedFilter, search: state.search }, 'reviews')
    try {
      const result = await getReviewExportApi(
        rootState.backoffice.currentClient.identifier,
        formId,
        urlParams,
        filterParams
      )
      const filename = getFileName(result.headers)
      downloadFile(result.data, filename, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
    } catch {
      dispatch(
        'backoffice/setAlert',
        {
          color: 'error',
          text: i18n.t('error.notification.default'),
        },
        { root: true }
      )
    }
    dispatch('backoffice/setWorking', false, { root: true })
  },

  async getConditionableFields({ commit, rootState }, formId) {
    const conditionableFields = await getConditionableFieldsApi(
      rootState.backoffice.currentClient.identifier,
      getShortLocal(rootState.backoffice.currentUser.locale),
      formId
    )
    commit(GET_CONDITIONABLE_FIELDS, conditionableFields.rows)
  },

  setCurrentLocaleTab({ commit }, currentLocaleTab) {
    commit(SET_CURRENT_LOCALE_TAB, currentLocaleTab)
  },
  async setCurrentFormSubmission({ commit, rootState }, { formId, reviewId }) {
    const currentFormSubmission = await getFormSubmissionApi(
      rootState.backoffice.currentClient.identifier,
      formId,
      reviewId
    )
    const formattedFormSubmission = flattenReview(currentFormSubmission)
    commit(SET_CURRENT_FORM_SUBMISSION, formattedFormSubmission)
  },
  async setCurrentReview({ commit, rootState }, { formId, reviewId }) {
    const currentReview = await getReviewApi(rootState.backoffice.currentClient.identifier, formId, reviewId)
    const formattedReview = flattenReview(currentReview)
    commit(SET_CURRENT_REVIEW, formattedReview)
  },
  setCurrentFilters({ commit }, currentFilters) {
    commit(
      SET_CURRENT_FILTERS,
      currentFilters.filter(filter => filter.value)
    )
  },
  //translations
  async setFormDefaultTranslations({ commit, rootState, dispatch }) {
    const defaultTranslations = await getFormDefaultTranslations(rootState.backoffice.currentClient.identifier)
    commit(
      SET_FORM_DEFAULT_TRANSLATIONS,
      toDotTranslationsObject(
        defaultTranslationsToList(defaultTranslations),
        defaultLangList.map(lang => lang.locale)
      )
    )
    dispatch('setFormMandatoryTranslations', getMandatoryTranslationKeys(defaultTranslations))
  },

  setFormMandatoryTranslations({ commit }, mandatoryTranslations) {
    commit(SET_FORM_MANDATORY_TRANSLATIONS, mandatoryTranslations)
  },
  //filters
  async setReviewsFilters({ commit, rootState }, formId) {
    const filtersList = await getReviewsFiltersApi(rootState.backoffice.currentClient.identifier, formId)
    commit(SET_REVIEWS_GENERIC_FILTER, filtersList.filters)
    commit(SET_REVIEWS_FIELD_FILTERS, filtersList.dynamicFilters)
  },

  // //forms
  async setCurrentForm({ commit, rootState, dispatch }, formId) {
    let [currentForm] = await Promise.all([
      getFormApi(rootState.backoffice.currentClient.identifier, formId),
      dispatch('getConditionableFields', formId),
      dispatch('setReviewsFilters', formId),
    ])

    const translationsDbo = {
      activatedLocales: currentForm.translations.activatedLocales,
      list: toDotTranslationsObject(currentForm.translations.list, currentForm.translations.activatedLocales),
    }

    commit(SET_CURRENT_FORM, {
      ...currentForm,
      translations: translationsDbo,
    })

    const userLocale = getShortLocal(rootState.backoffice.currentUser.locale)
    dispatch('setCurrentFormLexicon', {
      ...getPropertyByLocale(defaultReviewLexicon, userLocale),
      ...getPropertyByLocale(currentForm.settings.lexicon, userLocale),
    })

    // Get fields and statics
    dispatch('getCurrentFormsFields')
    dispatch('getCurrentFormsStatics')
  },
  async getCurrentFormsFields({ commit, state, rootState }) {
    const formsFields = await getFormFieldsApi(rootState.backoffice.currentClient.identifier, state.currentForm.id)
    commit(GET_CURRENT_FORMS_FIELDS, formsFields)
  },
  async getCurrentFormsStatics({ commit, state, rootState }) {
    const formsStatics = await getFormStaticsApi(rootState.backoffice.currentClient.identifier, state.currentForm.id)
    commit(GET_CURRENT_FORMS_STATICS, formsStatics)
  },
  async updateCurrentFormSettings({ commit, rootState }, { formId, settings }) {
    const formattedSettings = { ...settings }

    await putFormSettingsApi(rootState.backoffice.currentClient.identifier, formId, formattedSettings)

    commit(SET_CURRENT_FORM_SETTINGS, settings)
  },
  async updateCurrentFormsFields({ commit, rootState }, { formId, fields }) {
    await setFormFieldsApi(rootState.backoffice.currentClient.identifier, formId, fields)
    commit(SET_CURRENT_FORM_FIELDS, fields)
  },
  async updateCurrentFormsStatics({ commit, rootState }, { formId, statics }) {
    await setFormStaticsApi(rootState.backoffice.currentClient.identifier, formId, statics)
    commit(SET_CURRENT_FORM_STATICS, statics)
  },
  setActionDrawerOpened({ commit }, opened) {
    commit(SET_ACTION_DRAWER_OPENED, opened)
  },

  setCurrentActionDrawer({ commit }, currentActionDrawer) {
    commit(SET_CURRENT_ACTION_DRAWER, currentActionDrawer)
  },
  async updateCurrentFormTranslations({ commit, rootState }, { formId, translations }) {
    const translationsDto = {
      activatedLocales: translations.activatedLocales,
      list: toObjectTranslations(translations.list),
    }

    await putFormTranslationsApi(rootState.backoffice.currentClient.identifier, formId, translationsDto)
    commit(SET_CURRENT_FORM_TRANSLATIONS, translations)
  },

  setCurrentFormLexicon({ commit }, lexicon) {
    commit(SET_CURRENT_FORM_LEXICON, lexicon)
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
}

export { getFiltersParams } from '@/domains/review/services/reviewListTransform.service'

function toObjectTranslations(translations) {
  const translationsObject = {}

  for (const [translationKey, translationValue] of Object.entries(translations)) {
    if (translationKey.includes('.')) {
      const translationsKey = translationKey.split('.')

      let currentTranslations = translationsObject

      for (const subTranslationKey of translationsKey) {
        if (!currentTranslations[subTranslationKey]) {
          currentTranslations[subTranslationKey] = {}
        }
        currentTranslations = currentTranslations[subTranslationKey]
      }
      currentTranslations = Object.assign(currentTranslations, translationValue)
    } else {
      translationsObject[translationKey] = translationValue
    }
  }

  return translationsObject
}

function toDotTranslationsObject(translations, locales, dottedTranslations = {}, currentTranslation = '') {
  for (const translationKey in translations) {
    const translationValue = translations[translationKey]

    const newTranslationKey = currentTranslation ? `${currentTranslation}.${translationKey}` : translationKey

    if (translationValue && typeof translationValue === 'object' && !isI18nObject(translationValue, locales)) {
      toDotTranslationsObject(translationValue, locales, dottedTranslations, newTranslationKey)
    } else {
      dottedTranslations[newTranslationKey] = translationValue
    }
  }

  return dottedTranslations
}

function isI18nObject(value, locales) {
  return Object.keys(value).some(key => {
    return typeof key === 'string' && locales.includes(key)
  })
}

function defaultTranslationsToList(defaultTranslations) {
  if (defaultTranslations.values) {
    return defaultTranslations.values
  }
  const transformed = {}
  for (const [key, value] of Object.entries(defaultTranslations)) {
    transformed[key] = defaultTranslationsToList(value)
  }
  return transformed
}

function getMandatoryTranslationKeys(defaultTranslations) {
  const keys = []

  for (const [key, value] of Object.entries(
    toDotTranslationsObject(
      defaultTranslations,
      defaultLangList.map(lang => lang.locale)
    )
  )) {
    if (key.includes('.required') && value) {
      keys.push(key.replace('.required', ''))
    }
  }

  return keys
}
